import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  Watch,
} from '@stencil/core';

import { FOCUS_TRAP_INVALID_AUTOFOCUS } from '../../constants/constants';
import { EventUtils, TAB } from '../../utils/events/event-utils';
import { isTabbable, tabbable } from '../../utils/focus-trap/tabbable';
import { Utils } from '../../utils/utils';

/**
 * @displayName Focus Trap
 *
 * @excludeTag
 */
@Component({
  tag: 'fluid-focus-trap',
  styleUrl: 'fluid-focus-trap.scss',
  shadow: true,
})
export class FluidFocusTrap implements ComponentInterface {
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * If true, all @Watch methods are temporarily disabled.
   */
  private _disableWatch = false;

  /**
   * Array of all root element children that can receive :focus via
   * tab navigation.
   */
  private _tabbables: HTMLElement[];

  /**
   * The root element's owner #document.
   */
  private _doc: Document;

  /**
   * The root element child that currently has :focus. NOTE: this
   * references the actual tabbable element that has :focus irrespective
   * of Shadow DOM.
   */
  private _activeElement: HTMLElement;

  /**
   * The tabbable element that previously had :focus before the last
   * :focus change event occurred. NOTE: this reference is NOT limited
   * to root element children & is used to set _nodeToRestoreRef.
   */
  private _prevActiveElement: HTMLElement;

  /**
   * Reference to the "sentinel" element that is located directly BEFORE
   * the root element in the DOM. Used to determine when :focus should
   * be "looped" to the last tabbable element.
   */
  private _sentinelStartRef: HTMLElement;

  /**
   * Reference to the "sentinel" element that is located directly AFTER
   * the root element in the DOM. Used to determine when :focus should
   * be "looped" to the first tabbable element.
   */
  private _sentinelEndRef: HTMLElement;

  /**
   * Root element child that will automatically receive :focus when the
   * trap is activated (when "autoFocus" is true).
   */
  private _autoFocusNode: HTMLElement;

  /**
   * Element that should receive :focus when trap is deactivated.
   * References the last element to have :focus before trap is activated.
   */
  private _nodeToRestoreRef: HTMLElement;

  /**
   * If true, the next :focus event to occur within the Host element will
   * be ignored.
   */
  private _ignoreNextFocusEvent: boolean;

  /**
   * The last "keydown" event to occur within the Host element. Used to
   * determine tab navigation direction (i.e. forwards/backwards) on
   * tabbable root element children.
   */
  private _lastKeydownEvent: KeyboardEvent;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidFocusTrapElement;

  // ================================================================ //
  // -- Props

  /**
   * Container that holds all tabbable elements within the Focus Trap.
   * Must be located within Focus Trap's Slot.
   */
  @Prop({ mutable: true }) rootElement: HTMLElement;

  @Watch('rootElement')
  onRootElementChange() {
    this.setOwnerDocument();
    this.initializeRootElement();
  }

  /**
   * Whether Focus Trap is actively trapping :focus. When true, :focus
   * is automatically transferred to the Focus Trap (if :focus was
   * previously outside the root element). When false, Focus Trap will
   * attempt to restore :focus to the element that previously had :focus
   * before the trap was activated.
   */
  @Prop({ mutable: true }) enabled = false;

  @Watch('enabled')
  onEnabledChange() {
    if (this._disableWatch) return;
    this.enabled ? this.activateTrap() : this.deactivateTrap();
  }

  /**
   * If true, prevents Focus Trap from enabling itself automatically when
   * one of its children receives :focus.
   */
  @Prop({ mutable: true }) disableAutoActivation: boolean;

  /**
   * When true, :focus will be automatically transferred to the root
   * element's first tabbable child (or child element set by
   * "setAutoFocusNode") when Focus Trap is enabled. When false, :focus
   * is transferred to the root element itself (default).
   */
  @Prop({ mutable: true }) autoFocus: boolean;

  // ================================================================ //
  // -- Events

  /**
   * Event emitted when Focus Trap "enabled" Prop becomes true.
   */
  @Event() activated: EventEmitter<boolean>;

  /**
   * Event emitted when Focus Trap "enabled" Prop becomes false.
   */
  @Event() deactivated: EventEmitter<boolean>;

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setOwnerDocument - gets the root element's ownerDocument
   * @action: initializeRootElement - prepares root element for trap activation
   */
  connectedCallback(): void {
    this.setOwnerDocument();
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   * @action: activateTrap - begins trapping :focus if "enabled" is true
   */
  componentDidLoad(): void {
    this.initializeRootElement();
    this.activateTrap();
  }

  /**
   * Called just after the component updates. It's never called during the first render().
   */
  componentDidUpdate() {
    this._disableWatch = false;
  }

  // ================================================================ //
  // -- Event Listeners

  /**
   * Listener for "blur" events on the #document root. When Focus Trap is
   * INACTIVE & :focus changes OUTSIDE the root element, keeps track of
   * the previously active/:focused element (i.e. the blur event "target").
   * When Focus Trap becomes active/enabled, the previously active element
   * becomes the node that Focus Trap will restore :focus to when the trap
   * is deactivated. When Focus Trap is ACTIVE, this listener is disabled.
   */
  @Listen('blur', { target: 'document', capture: true })
  onDocumentBlur(e: FocusEvent) {
    if (
      !this.rootElement ||
      this.enabled ||
      !this._doc.hasFocus() ||
      !e.composedPath().length
    )
      return;
    this._prevActiveElement = e.composedPath()[0] as HTMLElement;
  }

  /**
   * Listener for "keydown" events on the host element. Stores each event
   * internally so that subsequent :focus listener can determine if tab
   * navigation is occurring forwards or backwards (i.e. tab vs. shift+tab).
   */
  @Listen('keydown', { capture: true })
  onHostKeyDown(e: KeyboardEvent) {
    if (!this.rootElement) return;
    this._lastKeydownEvent = e;
  }

  /**
   * Listener for :focus events on the host element (i.e. :focus events
   * that occur within the bounds of the trap). If Focus Trap is INACTIVE
   * when the :focus event occurs, activates the trap & transfers external
   * :focus to the root element (if event occurred on a "sentinel" element)
   * OR maintains :focus within the trap (if event occurred on a root
   * element child). If Focus Trap is already ACTIVE when the :focus event
   * occurs, determines if the event target is a "sentinel" element - if
   * so, loops :focus to the first or last tabbable element within the trap
   * (depending on the tab navigation direction).
   */
  @Listen('focus', { capture: true })
  onHostFocus(e: FocusEvent) {
    switch (true) {
      // Terminate if root element is undefined, next :focus event should be ignored, or auto-activation is disabled
      case !this.rootElement ||
        this._ignoreNextFocusEvent ||
        (this.disableAutoActivation && !this.enabled):
        this._ignoreNextFocusEvent = false;
        break;
      // If Focus Trap is NOT enabled, activate the trap (since event occurred within bounds of trap)
      case !this.enabled:
        this._disableWatch = true;
        this.enabled = true;
        this.activateTrap(!EventUtils.eventPathIncludes(e, this.rootElement));
        break;
      // If Focus Trap IS enabled, loop :focus to first/last tabbable element (if necessary)
      default:
        this.loopFocus();
    }
  }

  /**
   * Listener for "focusin" events on the #document root. When Focus
   * Trap is ACTIVE, ensures :focus stays within the bounds of the trap.
   * If :focus event occurs OUTSIDE the root element, resets :focus back
   * to the root element.
   */
  @Listen('focusin', { target: 'document' })
  onDocumentFocusIn(e: FocusEvent) {
    if (!this.rootElement || !this.enabled || !this._doc.hasFocus()) return;
    if (!EventUtils.eventPathIncludes(e, this.rootElement)) {
      this.transferFocus();
    }
  }

  // ================================================================ //
  // -- Public Methods

  /**
   * Refreshes _tabbables & returns the updated array of tabbable elements.
   */
  @Method()
  async updateTabbables(): Promise<HTMLElement[]> {
    this.getTabbables();
    return this._tabbables;
  }

  /**
   * Sets element to receive :focus when Focus Trap is ACTIVATED &
   * "autoFocus" is true. Element provided MUST be child of root element.
   * @param element - root element child to automatically receive :focus
   */
  @Method()
  async setAutoFocusNode(element: HTMLElement) {
    if (!isTabbable(element, true)) return;
    this._autoFocusNode = element;
  }

  /**
   * Sets element to receive :focus when Focus Trap is DEACTIVATED.
   * @param element - element to receive :focus when "enabled" becomes false
   */
  @Method()
  async setNodeToRestore(element: HTMLElement) {
    if (!isTabbable(element)) return;
    this._nodeToRestoreRef = element;
  }

  /**
   * Updates the root element's owner #document. Should be called if/when
   * root element is moved from one #document to another (e.g. Modal is
   * moved out of an <iframe> element).
   */
  @Method()
  async setOwnerDocument() {
    this._doc = this.rootElement?.ownerDocument || document;
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Transfers :focus into the Focus Trap when trap is enabled. If
   * "autoFocus" is true, sets :focus on first tabbable child of root
   * element OR node specified by "setAutoFocusNode()". If "autoFocus"
   * is false OR "_autoFocusNode" is undefined (i.e. none of root
   * element's children are tabbable), sets :focus on root element.
   */
  transferFocus(): void {
    if (!this.rootElement) return;

    // Validate existing _autoFocusNode OR set to first tabbable child of root element
    if (this.autoFocus && !this._tabbables.includes(this._autoFocusNode)) {
      !!this._autoFocusNode &&
        Utils.consoleWarn(this.host, FOCUS_TRAP_INVALID_AUTOFOCUS);
      this._autoFocusNode = this._tabbables[0];
    }

    Utils.setFocus(this._autoFocusNode ?? this.rootElement);
  }

  /**
   * If either "sentinel" element has :focus after a "tab" keydown event,
   * determines the navigation direction (i.e. tab = forwards, shift+tab
   * = backwards) & "loops" :focus to the first or last tabbable child of
   * the root element.
   */
  loopFocus(): void {
    this._activeElement = Utils.getActiveElement(this._doc) as HTMLElement;
    if (
      this._activeElement !== this._sentinelStartRef &&
      this._activeElement !== this._sentinelEndRef
    )
      return;

    this.getTabbables();
    if (!this._tabbables.length) return;

    const firstTabbable = this._tabbables[0];
    const lastTabbable = this._tabbables[this._tabbables.length - 1];
    const shiftTabPressed =
      EventUtils.isKey(this._lastKeydownEvent, TAB) &&
      this._lastKeydownEvent?.shiftKey;

    shiftTabPressed ? lastTabbable.focus() : firstTabbable.focus();
  }

  /**
   * Ensures that the root element can receive :focus (but is NOT
   * tabbable) & applies CSS class that removes ":focus outline" styles.
   */
  initializeRootElement(): void {
    if (!this.rootElement || this.rootElement.getAttribute('tabIndex') === '-1')
      return;

    this.rootElement.classList.add('fluid-focus-trap--root');
    if (!this.rootElement.hasAttribute('tabIndex')) {
      this.rootElement.setAttribute('tabIndex', '-1');
    }
  }

  /**
   * Finds & stores references to all root element children that can
   * receive :focus via tab navigation.
   */
  getTabbables(): void {
    if (!this.rootElement) return;
    this._tabbables = tabbable(this.rootElement, { includeContainer: true });
  }

  /**
   * Activates the Focus Trap if "enabled" Prop is true - gets all
   * tabbable root element children, sets the element to receive :focus
   * when trap is deactivated, transfers :focus into the trap (if
   * previously :focused element is located outside the root element), &
   * emits "activated" event.
   * @param transferFocus - if true, root element should receive :focus
   */
  activateTrap(transferFocus = true): void {
    if (!this.rootElement || !this.enabled) return;

    this._activeElement = Utils.getActiveElement(this._doc) as HTMLElement;

    this.getTabbables();
    this.initializeRootElement();
    this.setNodeToRestoreRef(this._activeElement);
    transferFocus && this.transferFocus();

    this.activated.emit(true);
  }

  /**
   * Deactivates the Focus Trap if "enabled" Prop is false - restores
   * :focus to the last element to have :focus before the trap was
   * activated (if it's defined) & emits "deactivated" event.
   */
  deactivateTrap(): void {
    if (!this.rootElement || this.enabled) return;

    if (this._nodeToRestoreRef) {
      this._nodeToRestoreRef.focus();
      this._nodeToRestoreRef = undefined;
    }

    this.deactivated.emit(true);
  }

  /**
   * Returns true if the provided element is outside the bounds of the
   * Focus Trap (i.e. element is NOT a root element child, "sentinel"
   * element, or the root element itself).
   * @param el - the element to test
   */
  isExternalElement(el: HTMLElement): boolean {
    const internalElements = [
      this.rootElement,
      this._sentinelStartRef,
      this._sentinelEndRef,
    ];
    return !this._tabbables.includes(el) && !internalElements.includes(el);
  }

  /**
   * Sets internal reference to the element that should receive :focus
   * when the trap is deactivated. Element to restore MUST be outside
   * the bounds of the Focus Trap to be accepted.
   * @param nodeToRestore - node to restore :focus to (optional)
   */
  setNodeToRestoreRef(nodeToRestore?: HTMLElement): void {
    if (this._nodeToRestoreRef) return;

    switch (true) {
      case !!nodeToRestore && this.isExternalElement(nodeToRestore):
        this._nodeToRestoreRef = nodeToRestore;
        break;
      case !!this._prevActiveElement &&
        this.isExternalElement(this._prevActiveElement):
        this._nodeToRestoreRef = this._prevActiveElement;
        break;
      default:
        // TODO: CHANGE TO NEAREST TABBABLE ELEMENT
        this._nodeToRestoreRef = this._doc.body;
    }
  }

  /**
   * Returns correct tabIndex for "sentinel" elements. If auto-activation
   * is disabled AND trap is NOT enabled, causes "sentinel" elements to be
   * skipped during tab navigation.
   */
  getSentinelTabIndex(): number {
    return this.disableAutoActivation && !this.enabled ? -1 : 0;
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <Host>
        <div
          tabIndex={this.getSentinelTabIndex()}
          role='presentation'
          aria-hidden='true'
          class='sentinel-start'
          ref={(el) => (this._sentinelStartRef = el)}
        />
        <slot />
        <div
          tabIndex={this.getSentinelTabIndex()}
          role='presentation'
          aria-hidden='true'
          class='sentinel-end'
          ref={(el) => (this._sentinelEndRef = el)}
        />
      </Host>
    );
  }
}
